"use client";

import * as React from "react";
import { twMerge } from "tailwind-merge";
import { NavigationIcon } from "../Icon/NavigationIcon";
import { Logo } from "../Logo";
import "./StackNav.css";

/**
 * StackNav — Sliding-pane navigation rail with unbounded depth.
 *
 * Each item can carry `children` of the same shape. Selecting an item that has
 * children pushes a new pane onto the navigation stack (sliding the rail one
 * level deeper); a back affordance pops the top pane. Items that have an
 * `href` (and no `children`) behave as leaves and call `onSelect` when clicked.
 *
 * The metaphor is a navigation stack — like a UIKit `UINavigationController`
 * but living inside a sidebar. Nesting is recursive: any pane can drill into
 * the next, so the same shape composes from one level deep to N levels deep
 * with no code changes.
 *
 * @example
 * ```tsx
 * const items: StackNavItem[] = [
 *   { id: "home", label: "Home", href: "/" },
 *   {
 *     id: "components",
 *     label: "Components",
 *     href: "/components",
 *     children: [
 *       { id: "button", label: "Button", href: "/components/button" },
 *       {
 *         id: "data",
 *         label: "Data",
 *         children: [
 *           { id: "table", label: "Table", href: "/components/data/table" },
 *         ],
 *       },
 *     ],
 *   },
 * ];
 *
 * <StackNav items={items} value="/components/button" />
 * ```
 */

export type StackNavItem = {
  /** Stable identifier. Required for groups; for leaves defaults to `href`. */
  id: string;
  /** Display label. */
  label: string;
  /**
   * Target URL for leaves. When omitted (or when `children` is non-empty and
   * `pushOnSelect` is false), the row acts as a "drill-in" affordance only.
   *
   * If both `href` and `children` are present, clicking the row both navigates
   * to `href` (a section overview) and pushes its sub-pane onto the stack.
   * This mirrors the Vercel/Linear/Edges dashboard pattern. Set
   * `pushOnSelect: false` to split the two: the row becomes a plain link and a
   * separate chevron button drills into the sub-pane.
   */
  href?: string;
  /** Optional leading-edge icon. Alias-friendly for item models shared with SideNav. */
  icon?: React.ReactNode;
  /** Optional trailing-edge content (badge, count, kbd hint, etc.). */
  trailing?: React.ReactNode;
  /** Optional leading-edge content. Overrides `icon` when both are provided. */
  leading?: React.ReactNode;
  /** Marks the leaf as a divider — purely visual, not focusable. */
  divider?: boolean;
  /** Renders the entry as an uppercase group label rather than a row. */
  groupLabel?: boolean;
  /** Nested children. Triggers the drill-in arrow and pushes a new pane. */
  children?: StackNavItem[];
  /**
   * When `false` and the row has both `children` and an `href`, selecting the
   * row only navigates — it does not push a sub-pane; the row's chevron becomes
   * a separate drill-in button, and a `value` matching the row keeps its own
   * pane open rather than auto-opening the children. Without an `href` the row
   * still drills in on click (there is nothing else it could do). Defaults to
   * `true`.
   */
  pushOnSelect?: boolean;
  /** Aria-label override for screen readers. */
  ariaLabel?: string;
};

export type StackNavLinkComponentProps = {
  href: string;
  "aria-label"?: string;
  className?: string;
  children?: React.ReactNode;
  "aria-current"?: "page" | undefined;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export type StackNavRowRenderProps = {
  item: StackNavItem;
  isActive: boolean;
  /**
   * Whether the item has children. Use it to decide whether to draw a drill-in
   * affordance.
   *
   * Note this reports child presence, not a guarantee about `select`: `select`
   * pushes a pane when children exist unless the item is an `href` row with
   * `pushOnSelect: false`, whose pane is pushed by `drillIn` instead.
   */
  hasChildren: boolean;
  /** Push the item's sub-pane without navigating — the chevron affordance for split rows. */
  drillIn: () => void;
  /**
   * Run StackNav's selection: report through `onSelect` and push the sub-pane
   * when the item has children.
   *
   * This does **not** navigate `href`. The built-in row navigates because it is
   * itself an anchor and the browser follows it; a custom row must render its
   * own anchor — `LinkComponent` below — for `href` to do anything.
   */
  select: (event: React.MouseEvent) => void;
  /**
   * The anchor component configured on the nav (`linkComponent`, defaulting to
   * a native `<a>`). Render `href` rows through this so router integration and
   * navigation keep working.
   */
  LinkComponent: React.ComponentType<StackNavLinkComponentProps>;
  /** Class list the built-in row uses, for custom rows that want its base styling. */
  className: string;
};

/**
 * Renders one row in place of the built-in one. Return any node; the pane stack,
 * back affordance, and drill-in logic stay with StackNav.
 */
export type StackNavRenderRow = (props: StackNavRowRenderProps) => React.ReactNode;

/**
 * Color overrides for the rail surface and row affordances. Any value accepted
 * by CSS (`#hex`, `rgb()`, `var(--token)`, etc.) is fine; each entry maps to a
 * CSS custom property on the rail container, so consumers can also override
 * the same variables in CSS instead of via props. See `Theming` below.
 */
export type StackNavTheme = {
  /** Rail container background. Defaults to `var(--color-background-surface)`. */
  background?: string;
  /** Active row background. Defaults to `var(--color-background-selected)`. */
  activeBackground?: string;
  /** Active row text color. Defaults to `var(--color-text-primary)`. */
  activeColor?: string;
  /** Hover row background. Defaults to `var(--color-background-hover)`. */
  hoverBackground?: string;
  /** Border color (rail border, header/footer separators, dividers). Defaults to `var(--color-border-default)`. */
  borderColor?: string;
};

/**
 * A logical group of root-pane items. Renders the group's `items` in order,
 * with a thin divider drawn between consecutive groups (and an optional
 * uppercase `title` label above the group). Mirrors the old SideNav sectioned
 * `topItems` API so grouped navigation composes without the consumer having to
 * hand-inject divider rows.
 */
export type StackNavGroup = {
  /** Stable identifier for the group (used as a React key). */
  id: string;
  /** Optional uppercase label rendered above the group's rows. */
  title?: string;
  /** Items belonging to this group. Each can recurse with `children`. */
  items: StackNavItem[];
};

export type StackNavProps = {
  /**
   * Root pane items. Each item can recurse with `children`. Mutually exclusive
   * with `groups` — when both are passed, `groups` wins and `items` is ignored
   * (a dev-build warning is logged).
   */
  items?: StackNavItem[];
  /**
   * Grouped root-pane items. When provided, the root pane renders each group's
   * rows in order with a divider between consecutive groups (and the group's
   * optional `title` as an uppercase label). Prefer this over hand-injecting
   * `divider`/`groupLabel` rows into a flat `items` array — the component owns
   * the separator rendering so every consumer gets consistent grouping.
   */
  groups?: StackNavGroup[];
  /**
   * Additional L1 items pinned to the bottom of the root pane. Behave identically
   * to `items` — leaves navigate, parents push a sub-pane on click, deep-links
   * via `value` open them. Use for "tool" sections that sit at the same level
   * as the primary IA but visually belong at the bottom (e.g., Settings,
   * Help, Inspector). A subtle border separates them from `items`.
   *
   * **Constraint:** L1 `id` and `href` values must be unique across `items` and
   * `bottomItems`. Stack tokens and `value` lookups use these as keys, so a
   * collision causes the wrong entry to resolve. Dev builds log a warning when
   * a duplicate is detected.
   */
  bottomItems?: StackNavItem[];
  /**
   * Active leaf identifier (matched against `href` or `id`). When set, the
   * StackNav auto-opens the path that contains it on first render and again
   * whenever `value` changes externally.
   */
  value?: string | null;
  /**
   * Initial open stack (array of ids from root to deepest pane). Ignored
   * after first render — for ongoing control use `stack` + `onStackChange`.
   */
  defaultStack?: string[];
  /**
   * Fully-controlled open stack. When provided, the component does not manage
   * its own pane state.
   */
  stack?: string[];
  /** Fires when the open pane stack changes (drill-in / back). */
  onStackChange?: (stack: string[]) => void;
  /**
   * Fires when a leaf row is clicked (after `href` is followed). Receives the
   * full item plus its ancestor breadcrumb. Use this to track activation
   * outside of router-driven flows.
   */
  onSelect?: (item: StackNavItem, breadcrumb: StackNavItem[]) => void;
  /**
   * Custom anchor component (Next.js `Link`, react-router `Link`, etc.).
   * Receives `href`, `className`, `aria-current`, `onClick`, and `children`.
   * Defaults to a native `<a>`.
   */
  linkComponent?: React.ComponentType<StackNavLinkComponentProps>;
  /**
   * Take over rendering of the rows, keeping this component's pane stack, back
   * affordance, and drill-in behavior.
   *
   * Needed when a row must carry its own interactive control — a checkbox, a
   * switch — which cannot go in `leading`, since that renders inside the row's
   * link/button, making it invalid markup and giving one click target two
   * actions. A custom row can place the control as a sibling of whatever it
   * makes navigable.
   *
   * Dividers and group labels are unaffected. Omit this and rows render exactly
   * as they always have.
   */
  renderRow?: StackNavRenderRow | undefined;
  /**
   * Render the Texture logo at the top of the rail (above any `header` slot).
   * Set `logo` to override the default mark; otherwise the standard `<Logo />`
   * with wordmark is rendered. Defaults to `false` for backward compatibility —
   * existing consumers that already place a logo inside `header` keep working
   * unchanged.
   */
  showLogo?: boolean;
  /**
   * Custom logo node rendered in the logo region when `showLogo` is true.
   * Ignored when `showLogo` is false. Use this to swap in a co-branded mark
   * (e.g. partner logo) without losing the standard logo layout/spacing.
   */
  logo?: React.ReactNode;
  /** Fixed top region (search trigger, breadcrumb, theme toggle). Rendered below the logo when both are present. */
  header?: React.ReactNode;
  /** Fixed bottom region (theme toggle, account menu). */
  footer?: React.ReactNode;
  /** Title shown above the root pane (optional — usually folded into header). */
  rootLabel?: string;
  /**
   * Whether the rail itself should size itself to the viewport and stick.
   * Defaults to `true`. Disable for embedded / non-fullheight contexts.
   */
  sticky?: boolean;
  /**
   * Width of the rail in pixels. Defaults to 260. Pass `null` to skip the
   * inline width style entirely (useful when an ancestor like AppShell controls
   * width via Tailwind classes).
   */
  width?: number | null;
  /** Aria-label for the rail's `<aside>`. Defaults to "Navigation". */
  ariaLabel?: string;
  /**
   * Per-instance color overrides for the rail surface and row affordances.
   * Each key maps to a CSS custom property (see {@link StackNavTheme}).
   * Anything left unset falls back to the corresponding `--edges-stack-nav-*`
   * variable on the rail, which itself defaults to a neutral Edges token.
   */
  theme?: StackNavTheme;
  /**
   * Enables the subtle pane slide/fade transition when drilling in or popping
   * back. Defaults to `true`; set `false` for tests, perf-sensitive embedded
   * contexts, or users/surfaces that need instant pane swaps.
   */
  animated?: boolean;
  /** Inline style passthrough on the rail container (merged with theme vars). */
  style?: React.CSSProperties;
  /** Extra classes appended to the rail container. */
  className?: string;
  /**
   * ID of AppShell's mobile-menu checkbox. When provided, the logo region
   * renders a mobile close-button that toggles this checkbox via `htmlFor`.
   * Usually wired automatically by AppShell — consumers using StackNav
   * standalone can leave this unset.
   */
  mobileMenuId?: string;
  /**
   * ID of AppShell's sidebar-collapse checkbox. When provided, the logo region
   * renders a desktop collapse toggle that targets this checkbox via `htmlFor`,
   * and rows emit `data-collapse-*` attributes so AppShell's CSS rules apply.
   */
  sidebarCollapseId?: string;
};

type Pane = {
  /** Stack path leading to this pane (excluding the pane's own root). */
  path: string[];
  /** Parent item for sub-panes (undefined for root). */
  parent?: StackNavItem;
  /** Items rendered in this pane. */
  items: StackNavItem[];
  /** Bottom-pinned items (root pane only). */
  bottomItems?: StackNavItem[];
};

function itemKey(item: StackNavItem): string {
  return item.id ?? item.href ?? item.label;
}

/**
 * Find the path of ids from the root that leads to an item matching `value`
 * (compared against `href` first, then `id`). Leaf matches return the parent
 * ids only. Parent matches with children return the parent's own id too so
 * section overview routes can keep their child pane open.
 */
function findPathTo(items: StackNavItem[], value: string, trail: string[] = []): string[] | null {
  for (const item of items) {
    if (item.href === value || item.id === value) {
      if (item.children?.length && item.pushOnSelect !== false) {
        return [...trail, itemKey(item)];
      }
      return trail;
    }
    if (item.children?.length) {
      const next = findPathTo(item.children, value, [...trail, itemKey(item)]);
      if (next) return next;
    }
  }
  return null;
}

/** Walk a path of ids from root and return the chain of items. */
function chainFromPath(items: StackNavItem[], path: string[]): StackNavItem[] {
  const chain: StackNavItem[] = [];
  let cursor = items;
  for (const id of path) {
    const next = cursor.find((entry) => itemKey(entry) === id);
    if (!next) break;
    chain.push(next);
    cursor = next.children ?? [];
  }
  return chain;
}

/** Build the array of panes from root → deepest, given a path of ids. */
function buildPanes(items: StackNavItem[], bottomItems: StackNavItem[], path: string[]): Pane[] {
  const rootChildren = bottomItems.length > 0 ? [...items, ...bottomItems] : items;
  const panes: Pane[] = [{ path: [], items, bottomItems: bottomItems.length > 0 ? bottomItems : undefined }];
  const chain = chainFromPath(rootChildren, path);
  let trail: string[] = [];
  for (const item of chain) {
    if (!item.children?.length) break;
    trail = [...trail, itemKey(item)];
    panes.push({ path: trail, parent: item, items: item.children });
  }
  return panes;
}

const DefaultLink: React.ComponentType<StackNavLinkComponentProps> = ({ href, ...rest }) => <a href={href} {...rest} />;

// Stable empty array so memoization doesn't bust when callers omit `bottomItems`.
const EMPTY_ITEMS: StackNavItem[] = [];

/**
 * Flatten grouped root items into a single `StackNavItem[]`, inserting a
 * non-focusable `divider` between consecutive groups and an uppercase
 * `groupLabel` row above any group that carries a `title`. Empty groups are
 * skipped so they never produce a dangling divider. This lets the rest of the
 * component (path-finding, pane building) keep operating on a flat array while
 * consumers express their IA as groups.
 */
function flattenGroups(groups: StackNavGroup[]): StackNavItem[] {
  const out: StackNavItem[] = [];
  for (const group of groups) {
    const groupItems = group.items ?? [];
    if (groupItems.length === 0) continue;
    if (out.length > 0) {
      out.push({ id: `__divider__${group.id}`, label: "", divider: true });
    }
    if (group.title) {
      out.push({ id: `__group__${group.id}`, label: group.title, groupLabel: true });
    }
    out.push(...groupItems);
  }
  return out;
}

/**
 * Dev-only invariant check: stack tokens and `value` lookups use `id`/`href`
 * as keys, so an L1 collision between `items` and `bottomItems` would silently
 * resolve to the `items` entry. Surface the conflict at the boundary instead
 * of letting it manifest as broken drill-in deep into the app.
 *
 * `findPathTo` matches `value` against `item.href === value || item.id === value`,
 * so id and href live in the same string namespace — `items[].id === "settings"`
 * collides with `bottomItems[].href === "settings"` just as surely as id↔id does.
 * Both ids and hrefs from items go into a single set, and any bottomItem's id or
 * href hitting that set is a real collision.
 */
function warnOnL1Collision(items: StackNavItem[], bottomItems: StackNavItem[]) {
  if (process.env.NODE_ENV === "production") return;
  if (bottomItems.length === 0) return;
  const topKeys = new Map<string, "id" | "href">();
  for (const item of items) {
    if (item.id) topKeys.set(item.id, "id");
    if (item.href && !topKeys.has(item.href)) topKeys.set(item.href, "href");
  }
  for (const item of bottomItems) {
    let dup: string | null = null;
    if (item.id && topKeys.has(item.id)) {
      const topField = topKeys.get(item.id);
      dup = topField === "id" ? `id "${item.id}"` : `id "${item.id}" (matches an items \`href\`)`;
    } else if (item.href && topKeys.has(item.href)) {
      const topField = topKeys.get(item.href);
      dup = topField === "href" ? `href "${item.href}"` : `href "${item.href}" (matches an items \`id\`)`;
    }
    if (dup) {
      console.warn(
        `[StackNav] L1 ${dup} appears in both \`items\` and \`bottomItems\`. ` +
          "Stack tokens and value lookups will resolve to the `items` entry, " +
          "making the bottomItems entry unreachable. L1 ids and hrefs must be " +
          "unique across both arrays."
      );
    }
  }
}

/**
 * Build the inline `style` object that wires the optional `theme` prop onto
 * the rail's CSS custom properties. Anything unset stays unset so the rail's
 * own `--edges-stack-nav-*` defaults (set in the base class) win.
 */
function buildThemeVars(theme: StackNavTheme | undefined): React.CSSProperties {
  if (!theme) return {};
  const vars: Record<string, string> = {};
  if (theme.background !== undefined) vars["--edges-stack-nav-background"] = theme.background;
  if (theme.activeBackground !== undefined) vars["--edges-stack-nav-row-active-background"] = theme.activeBackground;
  if (theme.activeColor !== undefined) vars["--edges-stack-nav-row-active-color"] = theme.activeColor;
  if (theme.hoverBackground !== undefined) vars["--edges-stack-nav-row-hover-background"] = theme.hoverBackground;
  if (theme.borderColor !== undefined) vars["--edges-stack-nav-border-color"] = theme.borderColor;
  return vars as React.CSSProperties;
}

export function StackNav({
  items,
  groups,
  bottomItems,
  value,
  defaultStack,
  stack: controlledStack,
  onStackChange,
  onSelect,
  linkComponent: LinkComponent = DefaultLink,
  renderRow,
  showLogo = false,
  logo,
  header,
  footer,
  mobileMenuId,
  sidebarCollapseId,
  rootLabel,
  sticky = true,
  width = 260,
  ariaLabel = "Navigation",
  theme,
  animated = true,
  style,
  className,
}: StackNavProps) {
  if (process.env.NODE_ENV !== "production" && groups && items) {
    console.warn(
      "[StackNav] Both `items` and `groups` were provided. `groups` takes precedence and `items` is ignored."
    );
  }

  // Resolve the effective root items: grouped items flatten into a single
  // array with dividers between groups; otherwise use the flat `items` prop.
  // Memoized on the actual inputs so a stable `groups`/`items` reference does
  // not re-flatten every render.
  const resolvedItems = React.useMemo(() => (groups ? flattenGroups(groups) : (items ?? EMPTY_ITEMS)), [groups, items]);

  const safeBottomItems = bottomItems ?? EMPTY_ITEMS;
  const lookupItems = React.useMemo(
    () => (safeBottomItems.length > 0 ? [...resolvedItems, ...safeBottomItems] : resolvedItems),
    [resolvedItems, safeBottomItems]
  );

  React.useEffect(() => {
    warnOnL1Collision(resolvedItems, safeBottomItems);
  }, [resolvedItems, safeBottomItems]);

  // Open stack (path of ids from root). Either fully controlled or local.
  const [internalStack, setInternalStack] = React.useState<string[]>(() => {
    if (controlledStack !== undefined) return controlledStack;
    if (defaultStack !== undefined) return defaultStack;
    if (value) {
      const path = findPathTo(lookupItems, value);
      return path ?? [];
    }
    return [];
  });

  const openStack = controlledStack ?? internalStack;

  const setStack = React.useCallback(
    (next: string[]) => {
      if (controlledStack === undefined) {
        setInternalStack(next);
      }
      onStackChange?.(next);
    },
    [controlledStack, onStackChange]
  );

  // When `value` or `items` change externally and we're not in fully-controlled
  // stack mode, sync the open pane to contain the active item. Skip the first
  // sync when `defaultStack` is explicitly provided so callers can choose an
  // initial pane (including the root `[]`) without it being immediately
  // overwritten by `value` on mount.
  const didRunValueSyncRef = React.useRef(false);
  React.useEffect(() => {
    if (controlledStack !== undefined) return;
    if (!didRunValueSyncRef.current) {
      didRunValueSyncRef.current = true;
      if (defaultStack !== undefined) return;
    }
    if (!value) return;
    const path = findPathTo(lookupItems, value);
    setInternalStack((prev) => {
      const next = path ?? [];
      return arraysEqual(prev, next) ? prev : next;
    });
  }, [value, lookupItems, controlledStack, defaultStack]);

  const panes = React.useMemo(
    () => buildPanes(resolvedItems, safeBottomItems, openStack),
    [resolvedItems, safeBottomItems, openStack]
  );

  const drillInto = React.useCallback(
    (item: StackNavItem, parentPath: string[]) => {
      if (!item.children?.length) return;
      setStack([...parentPath, itemKey(item)]);
    },
    [setStack]
  );

  const pop = React.useCallback(() => {
    setStack(openStack.slice(0, -1));
  }, [openStack, setStack]);

  const handleSelect = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>, item: StackNavItem) => {
      const breadcrumb = chainFromPath(lookupItems, openStack);
      onSelect?.(item, breadcrumb);

      const hasChildren = !!item.children?.length;
      // An href row with pushOnSelect: false is a plain link; its chevron button drills in.
      if (hasChildren && !(item.href && item.pushOnSelect === false)) {
        const parentPath = panes[panes.length - 1]?.path ?? [];
        drillInto(item, parentPath);
      }

      // Default link behavior handles `href` navigation; nothing else needed.
      if (!item.href) {
        event.preventDefault();
      }
    },
    [lookupItems, openStack, onSelect, drillInto, panes]
  );

  const handleDrillIn = React.useCallback(
    (item: StackNavItem) => {
      const parentPath = panes[panes.length - 1]?.path ?? [];
      drillInto(item, parentPath);
    },
    [drillInto, panes]
  );

  // Active matching: when `value` matches the leaf's `href` or `id`.
  const isActive = React.useCallback(
    (item: StackNavItem) => {
      if (!value) return false;
      return item.href === value || item.id === value;
    },
    [value]
  );

  const stickyClass = sticky ? "sticky top-0 h-[100dvh]" : "";

  const mergedStyle: React.CSSProperties = {
    ...(width != null ? { width } : {}),
    ...buildThemeVars(theme),
    ...style,
  };

  return (
    <aside
      role="navigation"
      aria-label={ariaLabel}
      data-edges-stack-nav=""
      className={twMerge(
        "edges-stack-nav flex flex-col flex-shrink-0 overflow-hidden",
        animated && "edges-stack-nav--animated",
        stickyClass,
        className
      )}
      style={mergedStyle}
    >
      {showLogo ? (
        <div className="edges-stack-nav__logo">
          <div className="relative top-[-1px]">{logo ?? <Logo showWordmark={true} width={120} />}</div>
          {sidebarCollapseId ? (
            <label
              htmlFor={sidebarCollapseId}
              className="hidden md:flex items-center justify-center w-8 h-8 rounded hover:bg-background-hover cursor-pointer transition-all duration-200 relative top-[-1px]"
              aria-label="Collapse sidebar"
            >
              <NavigationIcon name="Sidebar" />
            </label>
          ) : null}
          {mobileMenuId ? (
            <label
              htmlFor={mobileMenuId}
              className="md:hidden flex items-center justify-center w-8 h-8 rounded hover:bg-background-hover cursor-pointer transition-colors"
              aria-label="Close menu"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </label>
          ) : null}
        </div>
      ) : null}
      {header ? <div className="edges-stack-nav__header">{header}</div> : null}

      <div className="relative flex-1 overflow-hidden">
        {panes.map((pane, paneIndex) => {
          const depth = pane.path.length;
          const isTop = paneIndex === panes.length - 1;
          const isAbove = paneIndex < panes.length - 1;
          const paneKey = depth === 0 ? "__root__" : pane.path.join("/");

          return (
            <PaneView
              key={paneKey}
              pane={pane}
              parentPath={pane.path.slice(0, -1)}
              depth={depth}
              isTop={isTop}
              isAbove={isAbove}
              rootLabel={rootLabel}
              LinkComponent={LinkComponent}
              renderRow={renderRow}
              isActive={isActive}
              onSelect={handleSelect}
              onDrillIn={handleDrillIn}
              onBack={pop}
              animated={animated}
            />
          );
        })}
      </div>

      {footer ? <div className="edges-stack-nav__footer">{footer}</div> : null}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Internal pane view
// ---------------------------------------------------------------------------

type PaneViewProps = {
  pane: Pane;
  parentPath: string[];
  depth: number;
  isTop: boolean;
  isAbove: boolean;
  rootLabel?: string;
  LinkComponent: React.ComponentType<StackNavLinkComponentProps>;
  renderRow?: StackNavRenderRow | undefined;
  isActive: (item: StackNavItem) => boolean;
  onSelect: (event: React.MouseEvent<HTMLAnchorElement>, item: StackNavItem) => void;
  onDrillIn: (item: StackNavItem) => void;
  onBack: () => void;
  animated: boolean;
};

function PaneView({
  pane,
  depth,
  isTop,
  isAbove,
  rootLabel,
  LinkComponent,
  renderRow,
  isActive,
  onSelect,
  onDrillIn,
  onBack,
  animated,
}: PaneViewProps) {
  const positionClass = isTop
    ? "edges-stack-nav__pane--top"
    : isAbove
      ? "edges-stack-nav__pane--above"
      : "edges-stack-nav__pane--below";

  return (
    <nav
      aria-label={pane.parent?.label ?? rootLabel ?? "Navigation"}
      aria-hidden={!isTop ? true : undefined}
      // `inert` removes off-screen panes from focus order.
      inert={!isTop ? true : undefined}
      className={twMerge(
        "edges-stack-nav__pane absolute inset-0 flex flex-col gap-px overflow-y-auto px-2 py-3",
        !animated && "edges-stack-nav__pane--instant",
        positionClass
      )}
    >
      {depth > 0 && pane.parent ? (
        <button
          type="button"
          className="edges-stack-nav__back edges-stack-nav__row flex w-full cursor-pointer items-center gap-2 rounded-sm border-0 bg-transparent px-3 py-2 mb-2 text-left text-sm font-semibold text-text-primary"
          onClick={onBack}
          aria-label={`Back from ${pane.parent.label}`}
        >
          <ChevronLeftIcon />
          <span>{pane.parent.label}</span>
        </button>
      ) : rootLabel ? (
        <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-secondary">
          {rootLabel}
        </div>
      ) : null}

      {pane.items.map((item) => (
        <Row
          key={itemKey(item)}
          item={item}
          LinkComponent={LinkComponent}
          renderRow={renderRow}
          isActive={isActive(item)}
          onSelect={onSelect}
          onDrillIn={onDrillIn}
        />
      ))}

      {pane.bottomItems && pane.bottomItems.length > 0 ? (
        <div className="edges-stack-nav__bottom-items mt-auto flex flex-col gap-px pt-3">
          {pane.bottomItems.map((item) => (
            <Row
              key={itemKey(item)}
              item={item}
              LinkComponent={LinkComponent}
              renderRow={renderRow}
              isActive={isActive(item)}
              onSelect={onSelect}
              onDrillIn={onDrillIn}
            />
          ))}
        </div>
      ) : null}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

type RowProps = {
  item: StackNavItem;
  LinkComponent: React.ComponentType<StackNavLinkComponentProps>;
  renderRow?: StackNavRenderRow | undefined;
  isActive: boolean;
  onSelect: (event: React.MouseEvent<HTMLAnchorElement>, item: StackNavItem) => void;
  onDrillIn: (item: StackNavItem) => void;
};

function Row({ item, LinkComponent, renderRow, isActive, onSelect, onDrillIn }: RowProps) {
  if (item.divider) {
    return <div className="edges-stack-nav__divider my-2" role="separator" />;
  }

  if (item.groupLabel) {
    return (
      <div className="px-3 pt-6 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-secondary">
        {item.label}
      </div>
    );
  }

  const hasChildren = !!item.children?.length;
  const rowClass = twMerge(
    "edges-stack-nav__row flex items-center justify-between gap-3 rounded-sm px-3 py-2 text-sm font-normal text-text-primary no-underline cursor-pointer text-left bg-transparent border-0 transition-colors",
    isActive && "edges-stack-nav__row--active font-semibold"
  );

  if (renderRow) {
    return (
      <>
        {renderRow({
          item,
          isActive,
          hasChildren,
          select: (event) => onSelect(event as React.MouseEvent<HTMLAnchorElement>, item),
          drillIn: () => onDrillIn(item),
          LinkComponent,
          className: rowClass,
        })}
      </>
    );
  }

  const label = (
    <span className="flex min-w-0 items-center gap-2">
      {(item.leading ?? item.icon) ? (
        <span className="inline-flex items-center flex-shrink-0">{item.leading ?? item.icon}</span>
      ) : null}
      <span className="truncate">{item.label}</span>
    </span>
  );

  // Split row: the label is a plain link to the section's own page and the chevron is a
  // separate button that pushes the sub-pane. Neither may nest inside the other (a
  // button inside an anchor is invalid HTML), so the row's box is a wrapper that carries
  // the shared hover/active styling for both.
  if (item.href && hasChildren && item.pushOnSelect === false) {
    return (
      <div className={twMerge(rowClass, "cursor-default p-0")}>
        <LinkComponent
          href={item.href}
          aria-label={item.ariaLabel}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-sm px-3 py-2 text-inherit no-underline"
          aria-current={isActive ? "page" : undefined}
          onClick={(event) => onSelect(event, item)}
        >
          {label}
        </LinkComponent>
        <span className="flex flex-shrink-0 items-center gap-2 pr-2 text-text-secondary">
          {item.trailing}
          <button
            type="button"
            className="edges-stack-nav__drill-in inline-flex cursor-pointer items-center rounded-sm border-0 bg-transparent p-1 text-inherit"
            aria-label={`Open ${item.label}`}
            onClick={() => onDrillIn(item)}
          >
            <ChevronRightIcon />
          </button>
        </span>
      </div>
    );
  }

  const content = (
    <>
      {label}
      <span className="flex flex-shrink-0 items-center gap-2 text-text-secondary">
        {item.trailing}
        {hasChildren ? <ChevronRightIcon /> : null}
      </span>
    </>
  );

  if (item.href) {
    return (
      <LinkComponent
        href={item.href}
        aria-label={item.ariaLabel}
        className={rowClass}
        aria-current={isActive ? "page" : undefined}
        onClick={(event) => onSelect(event, item)}
      >
        {content}
      </LinkComponent>
    );
  }

  return (
    <button
      type="button"
      className={rowClass}
      aria-label={item.ariaLabel}
      onClick={(event) => {
        const ev = event as unknown as React.MouseEvent<HTMLAnchorElement>;
        onSelect(ev, item);
      }}
    >
      {content}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Icons + helpers
// ---------------------------------------------------------------------------

function ChevronRightIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4.5 3 7.5 6 4.5 9" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8.5 3 4.5 7 8.5 11" />
    </svg>
  );
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
